/**
 * "Replace it" card: exactly what to buy and where it goes, ready to hand to a handyman or the VA.
 * No price or link is stored (they go stale): the button opens the store's search for the brand and model.
 */
import { ExternalLink, Share2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { partSummary, partTypeDef, replaceEveryLabel, storeSearchUrl, type PropertyPart } from "@/lib/parts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function ReplaceItDialog({ part, onClose }: { part: PropertyPart | null; onClose: () => void }) {
  if (!part) return null;
  const photos = [part.photo_place_url, part.photo_label_url].filter(Boolean) as string[];
  const where = [part.room, part.place].filter(Boolean).join(" · ");
  const buy = [part.brand, part.model].filter(Boolean).join(" ") || part.name;
  const every = replaceEveryLabel(part.replace_every_months);

  const share = async () => {
    const text = [
      `Replace: ${part.name}`,
      partSummary(part),
      where ? `Where: ${where}` : "",
      `Buy: ${buy}${part.store ? ` (${part.store})` : ""}`,
      `Search: ${storeSearchUrl(part)}`,
    ]
      .filter(Boolean)
      .join("\n");
    try {
      if (navigator.share) {
        await navigator.share({ title: `Replace ${part.name}`, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast({ title: "Copied. Paste it to your handyman" });
      }
    } catch {
      /* cancelled */
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Replace it</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {photos.length > 0 ? (
            <div className="flex gap-2.5">
              {photos.map((u) => (
                <img key={u} src={u} alt="" className="h-[120px] flex-1 rounded-[16px] object-cover" />
              ))}
            </div>
          ) : null}
          <div>
            <div className="text-[20px] font-bold tracking-[-0.3px] text-[#0f1720]">{part.name}</div>
            <div className="text-[14.5px] font-bold text-[#475569]">{partSummary(part)}</div>
            {where ? <div className="mt-0.5 text-[13.5px] font-medium text-[#64748b]">{where}</div> : null}
            <div className="mt-0.5 text-[12.5px] font-bold text-[#8494a2]">
              {partTypeDef(part.part_type).label}
              {every ? ` · ${every}` : ""}
            </div>
          </div>
          <div className="rounded-[16px] bg-[#f4f7f9] p-3.5">
            <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">BUY</div>
            <div className="text-[16px] font-bold text-[#0f1720]">{buy}</div>
            <div className="text-[13px] font-medium text-[#64748b]">{part.store || "Any store"}</div>
          </div>
          <a
            href={storeSearchUrl(part)}
            target="_blank"
            rel="noreferrer"
            className="flex h-12 items-center justify-center gap-2 rounded-[14px] bg-[#15776d] text-[15.5px] font-bold text-white"
          >
            <ExternalLink className="h-4 w-4" />
            Find it at {part.store || "a store"}
          </a>
          <div className="text-center text-[12.5px] font-medium text-[#8494a2]">Opens the store's search for today's price and link.</div>
          <button type="button" onClick={() => void share()} className="flex h-12 items-center justify-center gap-2 rounded-[14px] bg-[#e2eeed] text-[15.5px] font-bold text-[#15776d]">
            <Share2 className="h-4 w-4" />
            Send to handyman
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
